import axios, {
    AxiosRequestConfig,
    AxiosResponse,
    Canceler
} from 'axios';

import {
    AppDispatch,
    store
} from '~/frontend/src/store';

import receiveBackendState from '~/frontend/src/actions/receiveBackendState';

import ajaxError from '~/frontend/src/actions/ajax/ajaxError';
import cancelRequest from '~/frontend/src/actions/ajax/cancelRequest';
import endRequest from '~/frontend/src/actions/ajax/endRequest';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import {Dispatch} from 'redux';
import logout from '~/frontend/src/actions/logout';
import LocalizableError from '~/shared/src/base/LocalizableError';
import StackError from '~/shared/src/base/StackError';
import {XhrError} from '~/frontend/src/reducers/xhr';


axios.defaults.headers.common[ 'X-Requested-With' ] = 'XMLHttpRequest';


type ErrorHandling = 'display' | 'throw';

/**
 * Perform xhr / ajax request incl. error handling
 *
 * @param {'post'|'get'} method
 * @param {string} url
 * @param {Object} options
 * @param {String} componentUid     The uid of the component the requests belongs to. Used to cancel all request for component.
 * @param {String} requestUid       The uid of the request itself. Used to cancel request.
 * @param {function} dispatch
 * @param {Object} data
 * @param {ErrorHandling} errorHandling
 *
 * @return {Promise<{data}>}
 */
async function doRequest( method : 'post' | 'get', url : string, options : AxiosRequestConfig = {}, componentUid? : string, requestUid? : string, dispatch? : AppDispatch, data? : any, errorHandling : ErrorHandling = 'display', isExternal : boolean = false ) : Promise<AxiosResponse<any> | null> {
    const CancelToken = axios.CancelToken;

    let result : AxiosResponse<any> | null = null,
        cancel : Canceler,
        requestEnded : boolean = false;

    dispatch = dispatch ?? store.dispatch;

    // dispatch endRequest exactly once per startRequest, regardless of which
    // completion path is taken (success, business error, thrown error, 401).
    // This prevents the request counter from drifting (stuck loading masks) when
    // overlapping requests share the same componentUid/requestUid.
    const finishRequest = () => {
        if ( !requestEnded && dispatch ) {
            requestEnded = true;
            dispatch( endRequest( cancel!, componentUid, requestUid ) );
        }
    };

    options = {
        ...options,
        withCredentials: options.withCredentials ?? true,
        cancelToken    : new CancelToken( ( c ) => {
            // c is the specific cancel function for this request. remember it as closure and via xhr reducer!
            cancel = c;

            if ( dispatch ) {
                dispatch( startRequest( cancel, componentUid, requestUid ) );
            }
        } )
    };

    try {
        result = await ( method === 'post'
            ? axios.post( url, data, options )
            : axios.get( url, options ) );

        if ( !isExternal ) {
            // for regular ajax requests, check success in response. For blob request (e.g. pdf), just check the http response header
            if ( result?.data?.success || ( options.responseType === 'blob' && result && result.headers?.[ 'yn-application-error' ] !== 'true' ) ) {
                if ( dispatch ) {
                    if ( result.headers[ 'db-environment' ] && result.headers[ 'ws-environment' ] ) {
                        dispatch( receiveBackendState( result.headers[ 'db-environment' ], result.headers[ 'ws-environment' ] ) );
                    }
                }
            }
            else {
                const error = ( options.responseType === 'blob' ? JSON.parse( await result?.data?.text() )?.error : result?.data?.error ) as XhrError;

                if ( dispatch && error ) {
                    const { title, message, number } = getErrorDetails( error );

                    if ( errorHandling === 'display' ) {
                        dispatch(
                            ajaxError(
                                title,
                                message,
                                number,
                                error?.errors,
                                cancel!,
                                componentUid,
                                requestUid
                            )
                        );
                    }
                    else if ( errorHandling === 'throw' ) {
                        const errorToThrow = new StackError( title );

                        if ( message ) {
                            errorToThrow.message = message;
                        }
                        if ( number ) {
                            errorToThrow.number = number;
                        }

                        for ( const e of error?.errors ?? [] ) {
                            errorToThrow.add( e );
                        }

                        throw ( errorToThrow );
                    }
                }
                else if ( componentUid !== 'login' || requestUid !== 'load' ) {
                    throw result?.data.error;
                }
                if ( options.responseType === 'blob' ) {
                    result = null;
                }
            }
        }
        else {
            result = result?.data;
        }
    }
    catch ( error : any ) {
        if ( error instanceof axios.Cancel ) {
            // request was cancelled; the cancel action already decremented the
            // counter, so we must not end the request again here
            requestEnded = true;
        }
        else if ( dispatch ) {
            // jump to login screen if we got a 401
            if ( error.response?.status === 401 ) {
                // except we get the error on login, then display an error message
                if ( requestUid === 'login' ) {
                    dispatch( ajaxError(
                        'Login',
                        error.response?.data?.error?.message,
                        undefined,
                        undefined,
                        cancel!,
                        componentUid,
                        requestUid
                    ) );
                }
                else {
                    void dispatch( logout() );
                }
            }
            else {
                if ( errorHandling === 'display' ) {
                    if ( !( error.name === 'AxiosError' && error.message === 'Network Error' ) ) {
                        let ynError : XhrError | undefined;

                        // try to handle backend error messages send along http status code
                        if ( error.response?.data?.error ) {
                            try {
                                ynError = error.response.data.error as XhrError;
                            }
                            catch ( e ) {
                                // do nothing
                            }
                        }

                        const { title, message, number } = ynError
                            ? getErrorDetails( ynError )
                            : {
                                title  : error.title ?? error.name ?? 'Error',
                                message: error.message ?? error.msg,
                                number : error.number ?? error.response?.status
                            };

                        dispatch( ajaxError(
                            title,
                            message,
                            number,
                            ynError?.errors,
                            cancel!,
                            componentUid,
                            requestUid
                        ) );
                    }
                    else {
                        console.error( error.response?.data?.error ?? error );
                    }
                }
                else if ( errorHandling === 'throw' ) {
                    throw ( error.response?.data?.error ?? error );
                }
            }
        }
    }
    finally {
        finishRequest();
    }

    return result;
}

/**
 * Aborts request
 *
 * @param {String} componentUid     The uid of the component the requests belongs to. Used to cancel all request for component.
 * @param {String} requestUid       The uid of the request itself. Used to cancel request.
 * @param {function} dispatch
 */
function doAbort( componentUid : string, requestUid? : string, dispatch? : Dispatch ) {
    dispatch = dispatch ?? store.dispatch;

    dispatch( cancelRequest( componentUid, requestUid ) );
}

const getErrorDetails = ( error : XhrError ) => {
    const retVal : {
        title : string;
        message? : string;
        number? : number;
    } = {
        title: 'Error'
    }

    if ( typeof error === 'string' ) {
        retVal.title = 'Backend error';
        retVal.message = error;
    }
    else if ( error.title ) {
        retVal.title = error.title;
        retVal.message = error.message;
        retVal.number = error.code;
    }
    else if ( error.type === 'stack' ) {
        const err = StackError.fromJSON( error );

        retVal.title = err.nameLocalized;
        retVal.message = err.messageLocalized;
        retVal.number = error.number;
    }
    else if ( error.type === 'localizable' ) {
        const err = LocalizableError.fromJSON( error );

        retVal.title = err.nameLocalized;
        retVal.message = err.messageLocalized;
        retVal.number = error.number;
    }
    else {
        retVal.title = error.name ?? retVal.title;
        retVal.message = error.message;
        retVal.number = error.number;
    }

    return retVal;
}
export default {

    /**
     * Wrapper for doRequest with method get.
     *
     * @param {String} url
     * @param {Object} options
     * @param {String} componentUid     The uid of the component the requests belongs to. Used to cancel all request for component.
     * @param {String} requestUid       The uid of the request itself. Used to cancel request.
     * @param {Function} dispatch
     * @param {ErrorHandling} errorHandling
     *
     * @return {Promise<AxiosResponse<any> | never>}
     */
    async get( url : string, options? : AxiosRequestConfig, componentUid? : string, requestUid? : string, dispatch? : Dispatch, errorHandling : ErrorHandling = 'display', isExternal : boolean = false ) : Promise<AxiosResponse<any> | null> {
        return doRequest( 'get', url, options, componentUid, requestUid, dispatch, undefined, errorHandling, isExternal );
    },

    /**
     * Wrapper for doRequest with method post.
     *
     * @param {String} url
     * @param {Object} data
     * @param {Object} options
     * @param {String} componentUid     The uid of the component the requests belongs to. Used to cancel all request for component.
     * @param {String} requestUid       The uid of the request itself. Used to cancel request.
     * @param {Function} dispatch
     * @param {ErrorHandling} errorHandling
     *
     * @return {Promise<AxiosResponse<any> | never>}
     */
    async post( url : string, data? : any, options? : AxiosRequestConfig, componentUid? : string, requestUid? : string, dispatch? : Dispatch, errorHandling : ErrorHandling = 'display', isExternal : boolean = false ) : Promise<AxiosResponse<any> | null> {
        componentUid = componentUid ?? ( data
            ? data.componentUid
            : null );

        requestUid = requestUid ?? ( data
            ? data.requestUid
            : null );

        if ( !data ) {
            data = {};
        }
        if ( typeof data === 'object' ) {
            data.webSocketId = global.webSocketId;
        }

        return doRequest( 'post', url, options, componentUid, requestUid, dispatch, data, errorHandling, isExternal );
    },

    /**
     * Wrapper for doAbort
     *
     * @param {String} componentUid     The uid of the component the requests belongs to. Used to cancel all request for component.
     * @param {String} requestUid       The uid of the request itself. Used to cancel request.
     * @param {Function} dispatch
     */
    abort( componentUid : string, requestUid? : string, dispatch? : Dispatch ) {
        doAbort( componentUid, requestUid, dispatch );
    }

};
